import { SVG } from '../../../../../../constants/assetLists';
import { Controller, useFormContext } from 'react-hook-form';
import AppMUIAutocomplete from '../../../../../../components/common/AppMUIAutoComplete';
import { AnnouncementFormValues } from '../types';
import MUIIconCheckboxSelect from '../../../../../../components/common/MUIIconCheckboxSelect';
import AppSwitch from '../../../../../../components/common/AppSwitch';
import { useQuery } from '@tanstack/react-query';
import QUERY_KEYS from '../../../../../../constants/queryKeys';
import { handleGetAllDepartmentsService } from '../../../../../../services/operations/departments';
import { handleGetAllUsersService } from '../../../../../../services/users';
import { useEffect, useMemo, useState } from 'react';
import {
  getCorrectPath,
  getDepartmentFilterParams,
  getRoleTypeName,
  getRoleTypeIcon,
} from '../../../../../../utils';
import { UsersListResponse } from '../../../../../users/components/UserScreens/users';
import { ApiResponse } from '../../../../../../services/axiosInstance';
import { ICONS } from '../../../../../users/data';
import React from 'react';
import { Chip } from '@mui/material';
import {
  ClientDataType,
  SubBranchData,
  useClientsContext,
} from '../../../../../../context/clients/ClientsContextProvider';
import BranchTreeAutocomplete from '../../../../../../components/common/BranchTreeAutocomplete';

const RESERVATION_DEPARTMENTS_STORAGE_KEY = 'defaultReservationDepartments';

type Props = {
  forForm: 'announcement' | 'task';
  index?: number;
  handleExpandSection?: (section: string) => void;
  expandedSections: string[];
};

function Recipients({
  forForm,
  index,
  handleExpandSection,
  expandedSections,
}: Props) {
  const {
    control,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<AnnouncementFormValues>();
  const [localDeptUI, setLocalDeptUI] = useState<string[]>([]);

  const { selectedClient, selectedLocation, allClients } = useClientsContext();

  const isClientSelected = selectedLocation?.isClient;
  const isBranchSelected = selectedLocation?.isBranch;
  const isSubBranchSelected = selectedLocation?.isSubBranch;
  const watchedBranchIds = watch('branchIds') ?? [];
  const watchedSubBranchIds = watch('subBranchIds') ?? [];

  const selectedBranchId: string[] = isClientSelected
    ? watchedBranchIds
    : isBranchSelected && watchedBranchIds.length
      ? watchedBranchIds
      : isSubBranchSelected && selectedLocation?.branchId
        ? [selectedLocation.branchId]
        : [];
  const selectedSubBranchId: string[] =
    isClientSelected || isBranchSelected
      ? watchedSubBranchIds
      : isSubBranchSelected && selectedLocation?.id
        ? [selectedLocation.id]
        : [];
  const selectedBranchKey = selectedBranchId.join('|');
  const selectedSubBranchKey = selectedSubBranchId.join('|');

  const announcementType = watch('announcementType');
  const departments = watch('departments');
  const isReservation = announcementType === 'Reservation';

  const fieldsShouldHide = ['Quick', 'Reservation'].includes(
    announcementType || '',
  );

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    (allClients ?? []).forEach((client: any) => {
      map.set(client.id, client.name);
      (client.branches ?? []).forEach((branch: any) => {
        map.set(branch.id, branch.name);
        (branch.subBranches ?? []).forEach((subBranch: any) => {
          map.set(subBranch.id, subBranch.name);
        });
      });
    });
    return map;
  }, [allClients]);

  const resolveLocationId = (
    entries: any[] | undefined,
    preferredIds: string[],
  ) => {
    const ids =
      entries
        ?.map((entry) => (typeof entry === 'string' ? entry : entry?._id))
        .filter(Boolean) ?? [];
    return (
      ids.find((id) => preferredIds.includes(id)) ??
      ids.find((id) => locationNameById.has(id))
    );
  };

  const getDeptGroupLabel = (department: any) => {
    const subBranchId = resolveLocationId(
      department.subBranchId,
      selectedSubBranchId ?? [],
    );
    if (subBranchId) return locationNameById.get(subBranchId) ?? '';

    const branchId = resolveLocationId(
      department.branchId,
      selectedBranchId ?? [],
    );
    if (branchId) return locationNameById.get(branchId) ?? '';

    const clientId = resolveLocationId(
      department.clientId,
      selectedClient?._id ? [selectedClient._id] : [],
    );
    return clientId ? (locationNameById.get(clientId) ?? '') : '';
  };

  const { data: allDepartments } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_ALL_DEPARTMENTS,
      selectedClient?._id,
      selectedBranchId,
      selectedSubBranchId,
    ],
    queryFn: () =>
      handleGetAllDepartmentsService({
        clientId: selectedClient?._id,
        ...(selectedBranchId.length > 0 && { branchId: selectedBranchId }),
        ...(selectedSubBranchId.length > 0 && {
          subBranchId: selectedSubBranchId,
        }),
      }),
    enabled: !!selectedClient?._id,
  });

  const { data: allUsers } = useQuery<
    ApiResponse<UsersListResponse>,
    Error,
    UsersListResponse
  >({
    queryKey: [
      QUERY_KEYS.GET_ALL_USERS,
      selectedClient?._id,
      selectedBranchId,
      selectedSubBranchId,
    ],
    queryFn: () =>
      handleGetAllUsersService({
        status: 'active',
        clientId: selectedClient?._id,
        ...(selectedBranchId.length > 0 && { branchId: selectedBranchId }),
        ...(selectedSubBranchId.length > 0 && {
          subBranchId: selectedSubBranchId,
        }),
        ...getDepartmentFilterParams(),
      }),
    enabled: !!selectedClient?._id,
  });

  const departmentOptions = useMemo(() => {
    if (allDepartments && allDepartments?.departments?.length > 0) {
      const departmentsByLocation = allDepartments.departments
        .map((department: any) => ({
          label: department.name,
          value: department._id,
          icon: department.icon.startsWith('icon')
            ? ICONS.find((icon) => icon.id === department.icon)?.icon || ''
            : getCorrectPath(department.icon, 'image'),
          groupLabel: getDeptGroupLabel(department),
        }))
        .sort((a: { groupLabel: string }, b: { groupLabel: string }) =>
          a.groupLabel.localeCompare(b.groupLabel),
        );

      return [
        {
          label: 'כל המחלקות',
          value: 'all',
          icon: SVG.saveAllIcon,
          groupLabel: '',
        },
        ...departmentsByLocation,
      ];
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allDepartments,
    locationNameById,
    selectedBranchKey,
    selectedSubBranchKey,
  ]);

  const restaurantDepartment = useMemo(
    () => departmentOptions.find((department) => department.label === 'מסעדה'),
    [departmentOptions],
  );

  const userOptions = useMemo(() => {
    if (allUsers && allUsers?.data?.length > 0) {
      // Filter out duplicates based on user._id
      const uniqueUsers = allUsers.data.filter(
        (user: any, index: number, self: any[]) =>
          index === self.findIndex((u: any) => u._id === user._id),
      );

      return uniqueUsers.map((user: any) => ({
        value: user._id,
        title: `${user.firstName} ${user.lastName}`,
        role: user.roles,
      }));
    }
    return [];
  }, [allUsers]);

  const isExpanded =
    forForm === 'announcement' ||
    expandedSections.includes(`task-recipients-${index}`);

  const departmentsFieldName = 'departments';
  const assignedToFieldName =
    forForm === 'task'
      ? (`tasks.${index}.recipients` as keyof AnnouncementFormValues)
      : 'assignee';

  const branchOptions = (allClients ?? [])
    .filter((c) => c.id === selectedClient?._id)
    .filter((c) => (c.branches?.length ?? 0) > 0) as ClientDataType[];

  const selectedBranchHasSubBranches = useMemo(() => {
    if (!selectedLocation?.isBranch || !selectedLocation.id) return false;
    const client = branchOptions[0];
    const branch = client?.branches?.find((b) => b.id === selectedLocation.id);
    return (branch?.subBranches?.length ?? 0) > 0;
  }, [branchOptions, selectedLocation]);

  const handleHideShowSection = () => {
    handleExpandSection?.(`task-recipients-${index}`);
  };

  const handleDepartmentsChange = (
    e: React.SyntheticEvent,
    value: { value: string }[] | null,
  ) => {
    const text = e.currentTarget.textContent;
    const isAllSelectedByText = text === 'כל המחלקות'; // Hebrew: "All departments"
    const currentDepartments = getValues().departments || [];

    const selectedValues = value?.map((item) => item.value) || [];
    const allDeptSelected = allDepartments?.departments?.every((dept: any) =>
      selectedValues.includes(dept._id),
    );
    const hasAllInSelection = selectedValues.includes('all');
    const hadAllPreviously = currentDepartments.includes('all');

    let newValue: string[] = [];
    if (hadAllPreviously && !hasAllInSelection) {
      newValue = []; // reset
    } else if (hadAllPreviously) {
      newValue = selectedValues.filter((v) => v !== 'all');
    } else if (isAllSelectedByText || hasAllInSelection || allDeptSelected) {
      newValue = ['all'];
    } else {
      newValue = selectedValues;
    }
    setValue(departmentsFieldName, newValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    if (isReservation && !restaurantDepartment) {
      if (newValue.length > 0) {
        localStorage.setItem(
          RESERVATION_DEPARTMENTS_STORAGE_KEY,
          JSON.stringify(newValue),
        );
      } else {
        localStorage.removeItem(RESERVATION_DEPARTMENTS_STORAGE_KEY);
      }
    }

    trigger(departmentsFieldName);
    // Update UI state
    if (newValue.includes('all')) {
      setLocalDeptUI(departmentOptions.map((o) => o.value));
    } else {
      setLocalDeptUI(newValue);
    }
  };

  useEffect(() => {
    if (
      forForm !== 'announcement' ||
      !isReservation ||
      restaurantDepartment ||
      departmentOptions.length === 0
    ) {
      return;
    }

    const currentDepartments = getValues(departmentsFieldName);
    if (Array.isArray(currentDepartments) && currentDepartments.length > 0) return;

    try {
      const storedDepartments = JSON.parse(
        localStorage.getItem(RESERVATION_DEPARTMENTS_STORAGE_KEY) || '[]',
      );
      const validStoredDepartments = Array.isArray(storedDepartments)
        ? storedDepartments.filter((departmentId) =>
            departmentOptions.some(
              (department) => department.value === departmentId,
            ),
          )
        : [];

      if (validStoredDepartments.length > 0) {
        setValue(departmentsFieldName, validStoredDepartments, {
          shouldValidate: true,
        });
      }
    } catch {
      localStorage.removeItem(RESERVATION_DEPARTMENTS_STORAGE_KEY);
    }
  }, [
    departmentOptions,
    departmentsFieldName,
    forForm,
    getValues,
    isReservation,
    restaurantDepartment,
    setValue,
  ]);

  useEffect(() => {
    const setLocalDeptUIIfChanged = (next: string[]) => {
      const isSame =
        localDeptUI.length === next.length &&
        localDeptUI.every((value, index) => value === next[index]);
      if (!isSame) setLocalDeptUI(next);
    };

    if (departments && departments?.length > 0) {
      if (departments === 'all') {
        setLocalDeptUIIfChanged(departmentOptions.map((o) => o.value));
      } else if (
        Array.isArray(departments) &&
        departments.length > 0 &&
        departments[0] !== 'all'
      ) {
        setLocalDeptUIIfChanged(departments);
      }
    } else {
      setLocalDeptUIIfChanged([]);
    }
  }, [departments, departmentOptions, localDeptUI]);

  const handleFormChange = (value: string[]) => {
    setValue(assignedToFieldName, value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    // Trigger validation for the assignee field
    trigger(assignedToFieldName);
  };

  const formSubBranchIds = watch('subBranchIds');

  const displayBranchTree =
    forForm == 'announcement' &&
    !fieldsShouldHide &&
    ((selectedLocation?.isClient && branchOptions.length > 0) ||
      (selectedLocation?.isBranch && selectedBranchHasSubBranches));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-start">
        <div
          className="flex items-center gap-4 cursor-pointer"
          onClick={handleHideShowSection}
        >
          <div className="w-[24px] h-[24px]">
            <img
              src={SVG.peopleIcon}
              alt="icon"
              className="w-full h-full object-contain"
            />
          </div>
          <p className="m-0 text-[20px] font-medium text-black">הוספת נמענים</p>
        </div>
      </div>

      {isExpanded && (
        <div className="flex flex-col gap-2">
          {displayBranchTree && (
            <Controller
              name="branchIds"
              control={control}
              render={({ field: { value: branchIds } }) => (
                <BranchTreeAutocomplete
                  allClients={branchOptions}
                  value={[...(branchIds || []), ...(formSubBranchIds || [])]}
                  onChange={(val) => {
                    const prevBranchIds = getValues('branchIds') || [];

                    const newBranchIds =
                      val
                        ?.filter((item) => item?.isBranch)
                        ?.map((item) => item?.id) || [];
                    const newSubBranches = (val?.filter(
                      (item) => item?.isSubBranch,
                    ) || []) as SubBranchData[];

                    const removedBranchIds = prevBranchIds.filter(
                      (id) => !newBranchIds.includes(id),
                    );

                    const filteredSubBranches = newSubBranches.filter(
                      (sub) => !removedBranchIds.includes(sub.branchId),
                    );

                    const autoAddBranchIds = filteredSubBranches
                      .filter((sub) => !newBranchIds.includes(sub.branchId))
                      .map((sub) => sub.branchId);

                    const finalBranchIds = Array.from(
                      new Set([...newBranchIds, ...autoAddBranchIds]),
                    );
                    const finalSubBranchIds = filteredSubBranches.map(
                      (sub) => sub.id,
                    );

                    setValue('branchIds', finalBranchIds, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    setValue('subBranchIds', finalSubBranchIds, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    setValue('departments', [], {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    setValue('assignee', [], {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    setValue('checklists', [], {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    const tasks = getValues('tasks') || [];
                    tasks.forEach((_, i) => {
                      setValue(`tasks.${i}.recipients`, [], {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      setValue(`tasks.${i}.checklists`, [], {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    });
                  }}
                />
              )}
            />
          )}

          <div className="flex extraSmall:flex-wrap sm:flex-nowrap gap-2">
            {forForm == 'announcement' && (
              <Controller
                control={control}
                name={departmentsFieldName}
                render={({ field: { value: departments } }) => (
                  <MUIIconCheckboxSelect
                    label="מחלקה/ות"
                    options={departmentOptions}
                    groupBy={(option) => option.groupLabel || ''}
                    size="small"
                    fullWidth
                    // required
                    disableCloseOnSelect
                    value={departmentOptions.filter((item) =>
                      localDeptUI.includes(item.value),
                    )}
                    onChange={handleDepartmentsChange}
                    renderValue={(selectedOptions, getTagProps) => {
                      const allSelected =
                        localDeptUI.includes('all') ||
                        localDeptUI.length === departmentOptions.length;

                      const renderChip = (
                        option: (typeof departmentOptions)[0],
                        index = 0,
                      ) => (
                        <Chip
                          label={option.label}
                          icon={
                            <img
                              src={option.icon}
                              alt="icon"
                              className="w-[18px] h-[18px] object-contain"
                            />
                          }
                          {...getTagProps({ index })}
                        />
                      );

                      if (allSelected) {
                        const allOption = departmentOptions.find(
                          (o) => o.value === 'all',
                        );
                        return allOption ? renderChip(allOption) : null;
                      }

                      return selectedOptions.map((option, index) =>
                        renderChip(option, index),
                      );
                    }}
                    error={!!errors.departments}
                    helperText={errors.departments?.message}
                  />
                )}
              />
            )}

            {!fieldsShouldHide && (
              <Controller
                control={control}
                name={assignedToFieldName}
                render={({ field: { value } }) => (
                  <AppMUIAutocomplete
                    label="שייוך אישי"
                    size="small"
                    options={userOptions}
                    ListboxProps={{
                      sx: {
                        maxHeight: 300,
                      },
                    }}
                    fullWidth
                    multiple
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.title}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      const index = (props as any)['data-option-index'] || 0;
                      const bgColor = option?.role?.[0]?.color || '#E0E0E0';

                      return (
                        <li
                          key={option.value}
                          {...optionProps}
                          className="flex flex-col"
                        >
                          <div className="flex">
                            <div
                              className={`w-[32px] h-[32px] rounded-full me-2 flex items-center justify-center`}
                              style={{ backgroundColor: bgColor }}
                            >
                              <span className="text-xl text-black">
                                {option.title.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <span>{option.title}</span>
                              <div className="flex flex-wrap gap-2 mt-1 mb-3">
                                {option.role && option.role.length > 0 && (
                                  <span
                                    className="text-xs text-grayText px-2 py-0.5 rounded-full flex items-center gap-1"
                                    style={{ backgroundColor: bgColor }}
                                  >
                                    {
                                      <img
                                        src={
                                          ICONS.find(
                                            (item) =>
                                              item.id ===
                                              (getRoleTypeIcon(
                                                option?.role?.[0]?.roleType,
                                              ) || option?.role?.[0]?.icon),
                                          )?.icon
                                        }
                                        alt=""
                                        className="h-3 w-3"
                                      />
                                    }
                                    <span>
                                      {getRoleTypeName(
                                        option?.role?.[0]?.roleType,
                                      ) || ''}
                                    </span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    }}
                    value={
                      userOptions.filter((item: any) =>
                        (value as string[])?.includes(item.value),
                      ) || []
                    }
                    onChange={(_, newValue) => {
                      const values = Array.isArray(newValue)
                        ? newValue.map((item) => item.value)
                        : [];
                      handleFormChange(values);
                    }}
                    error={
                      forForm === 'task'
                        ? errors?.tasks?.length
                          ? errors?.tasks[index!]?.recipients?.message
                            ? true
                            : false
                          : false
                        : !!errors.assignee
                    }
                    helperText={
                      forForm === 'task'
                        ? errors?.tasks?.length
                          ? errors?.tasks[index!]?.recipients?.message
                          : ''
                        : errors.assignee?.message
                    }
                  />
                )}
              />
            )}
          </div>

          {forForm == 'announcement' && !fieldsShouldHide && (
            <div className="flex flex-col gap-1 mt-3">
              <Controller
                control={control}
                name="enableRespondentCount"
                render={({ field: { onChange, value } }) => (
                  <div className="flex items-center gap-2">
                    <AppSwitch onChange={onChange} checked={value} />{' '}
                    <p
                      className={`m-0 text-[18px] text-darkerGray ${value ? 'font-semibold' : ''}`}
                    >
                      הפעלת ספירת משיבים
                    </p>
                  </div>
                )}
              />
              {errors.enableRespondentCount && (
                <p className="text-red-500 text-sm m-0">
                  {errors.enableRespondentCount.message}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Recipients;
